import { isImageSurface } from "./trace-reference";
import { sourceMeters } from "./source-scale";
import { createShapeId, createBindingId } from "@tldraw/tlschema";
import type { Editor, VecLike } from "./canvas";
import type { TLShape, TLShapeId, TLImageShape } from "@tldraw/tlschema";
import type { Sheet, Trace } from "../shared/schema";
import { editableTarget, putOnTrace } from "./tracing";
import { type Rect } from "../shared/trace-geometry";

/** A tracing sheet belongs to the board. Overlapping a drawing never attaches it. */
export function newTracingSheet(
  editor: Editor,
  over?: TLShape,
  placement?: Rect,
) {
  if (editor.getIsReadonly()) return;
  const bounds = over && editor.getShapePageBounds(over);
  const viewport = editor.getViewportPageBounds();
  const w = placement?.w ?? bounds?.w ?? Math.min(900, viewport.w * 0.65);
  const h = placement?.h ?? bounds?.h ?? w * 0.707;
  const id = createShapeId();
  const number =
    editor.getCurrentPageShapes().filter((s) => s.type === "trace").length + 1;
  editor.markHistoryStoppingPoint("Lay down tracing paper");
  editor.setCurrentTool("select");
  editor.createShape<Trace>({
    id,
    type: "trace",
    opacity: 1,
    parentId: editor.getCurrentPageId(),
    x: placement?.x ?? (bounds ? bounds.x + 35 : viewport.center.x - w / 2),
    y: placement?.y ?? (bounds ? bounds.y + 35 : viewport.center.y - h / 2),
    props: { w, h, name: `Tracing sheet ${number}`, hidden: false },
  });
  editor.select(id);
  return id;
}

export function liftTracingSheet(editor: Editor, trace: Trace) {
  if (!editableTarget(editor, trace)) return;
  const inheritedScale = !trace.parentId.startsWith("page:")
    ? sourceMeters(editor, trace)
    : null;
  editor.markHistoryStoppingPoint("Lift tracing paper");
  editor.run(() => {
    // Older boards may contain attached traces. Release those without shifting artwork.
    editor.reparentShapes([trace.id], editor.getCurrentPageId());
    if (inheritedScale !== null)
      editor.updateShape({
        id: trace.id,
        type: "trace",
        meta: {
          ...trace.meta,
          metersPerUnit: inheritedScale,
          calibration: inheritedScale
            ? "From original drawing"
            : "Not to Scale",
        },
      });
    editor.bringToFront([trace.id]);
    editor.select(trace.id);
  });
  editor.setCurrentTool("select");
}

/** Keep the container ID: artwork, bindings and comment anchors stay attached. */
export function convertTracingSheet(editor: Editor, id: TLShapeId) {
  const trace = editor.getShape(id);
  if (trace?.type !== "trace" || !editableTarget(editor, trace)) return;
  const transform = editor.getShapePageTransform(trace);
  const position = transform.applyToPoint({ x: 0, y: 0 });
  const metersPerUnit = sourceMeters(editor, trace);
  const { paperOpacity: _opacity, ...meta } = trace.meta;
  const sheet: Sheet = {
    ...trace,
    type: "sheet",
    parentId: editor.getCurrentPageId(),
    x: position.x,
    y: position.y,
    rotation: transform.rotation(),
    index: trace.parentId.startsWith("page:")
      ? trace.index
      : editor.getHighestIndexForParent(editor.getCurrentPageId()),
    opacity: 1,
    meta,
    props: {
      w: trace.props.w,
      h: trace.props.h,
      name: trace.props.name,
      source: "",
      preview: "",
      page: 1,
      paper: "Custom",
      metersPerUnit,
      calibration: metersPerUnit
        ? String(trace.meta.calibration || "From tracing paper")
        : "Not calibrated",
      revision: "01",
    },
  };
  editor.markHistoryStoppingPoint("Convert tracing paper to sheet");
  editor.run(() => {
    // updateShape merges props; replacing the complete record removes trace-only
    // props without deleting the container or touching any of its children.
    editor.store.put([sheet]);
    editor.select(sheet.id);
  });
  editor.setCurrentTool("select");
  return sheet;
}

/** Only visible objects physically below this paper are candidates. */
export function objectsUnderPaper(
  editor: Editor,
  trace: Trace,
  includeGroupContents = false,
) {
  const sorted = editor.getCurrentPageShapesSorted();
  const below = sorted.slice(
    0,
    sorted.findIndex((s) => s.id === trace.id),
  );
  const bounds = editor.getShapePageBounds(trace);
  return below.filter((s) => {
    if (
      (["sheet", "trace"].includes(s.type) &&
        !(includeGroupContents && isImageSurface(s))) ||
      editor.isShapeHidden(s)
    )
      return false;
    if (
      !includeGroupContents &&
      editor.getShape(s.parentId as TLShapeId)?.type === "group"
    )
      return false;
    const b = editor.getShapePageBounds(s);
    return !!bounds && !!b && bounds.collides(b);
  });
}
export function insidePaper(editor: Editor, trace: Trace, point: VecLike) {
  const p = editor.getPointInShapeSpace(trace, point);
  return p.x >= 0 && p.y >= 0 && p.x <= trace.props.w && p.y <= trace.props.h;
}
export function objectUnderPoint(
  editor: Editor,
  trace: Trace,
  point: VecLike,
  imageArea = false,
) {
  if (!insidePaper(editor, trace, point)) return;
  const allowed = new Set(
    objectsUnderPaper(editor, trace, imageArea).map((s) => s.id),
  );
  const root = (s: TLShape) => {
    if (imageArea) return s;
    let parent = editor.getShape(s.parentId as TLShapeId);
    while (parent?.type === "group") {
      s = parent;
      parent = editor.getShape(s.parentId as TLShapeId);
    }
    return s;
  };
  // The SDK hits group contents, not group containers. Return the intact group.
  const hit = editor.getShapeAtPoint(point, {
    hitInside: true,
    hitLocked: true,
    hitFrameInside: imageArea,
    filter: (s) => allowed.has(root(s).id) && (!imageArea || isImageSurface(s)),
  });
  return hit ? root(hit) : undefined;
}

/** Copy editable records and internal bindings, preserving their position on the table. */
export function copyObjectsToPaper(
  editor: Editor,
  traceId: TLShapeId,
  ids: TLShapeId[],
) {
  const trace = editor.getShape<Trace>(traceId);
  if (trace?.type !== "trace" || !editableTarget(editor, trace))
    throw new Error("This tracing sheet is no longer editable.");
  const allowed = new Set(objectsUnderPaper(editor, trace).map((s) => s.id));
  if (!ids.length || ids.some((id) => !allowed.has(id)))
    throw new Error("An item moved or was removed. Choose it again.");
  const content = editor.getContentFromCurrentPage(ids);
  if (!content) return [];
  // Image children may themselves have legacy attached traces: do not copy those discussions/options.
  const excluded = new Set<TLShapeId>();
  for (const s of content.shapes) {
    if (s.type === "trace" || excluded.has(s.parentId as TLShapeId))
      excluded.add(s.id);
  }
  const shapes = content.shapes.filter((s) => !excluded.has(s.id));
  for (const s of shapes) {
    if (s.type !== "image") continue;
    const a = s.props.assetId && editor.getAsset(s.props.assetId);
    if (!a || a.type !== "image" || !a.props.src)
      throw new Error("Wait for the image upload to finish.");
  }
  const map = new Map(shapes.map((s) => [s.id, createShapeId()]));
  const roots = new Set(content.rootShapeIds);
  editor.markHistoryStoppingPoint("Copy from underneath");
  editor.run(() => {
    const copies = shapes.map((s) => {
      const root = roots.has(s.id);
      const p = root ? editor.getPointInShapeSpace(trace, s) : s;
      const copy = {
        ...s,
        id: map.get(s.id)!,
        isLocked: false,
        parentId: root ? trace.id : map.get(s.parentId as TLShapeId)!,
        x: p.x,
        y: p.y,
        rotation: root
          ? s.rotation - editor.getShapePageTransform(trace).rotation()
          : s.rotation,
        index: root ? undefined : s.index,
        meta:
          s.type === "image"
            ? {
                ...s.meta,
                metersPerUnit: 0,
                calibrationWidth: s.props.w,
                calibration: "Not to Scale",
                scaleReview: false,
              }
            : s.meta,
      } as TLShape;
      return copy;
    });
    editor.createShapes(copies);
    editor.createBindings(
      (content.bindings ?? [])
        .filter((b) => map.has(b.fromId) && map.has(b.toId))
        .map((b) => ({
          ...b,
          id: createBindingId(),
          fromId: map.get(b.fromId)!,
          toId: map.get(b.toId)!,
        })),
    );
    editor.select(trace.id);
  });
  return content.rootShapeIds.map((id) => map.get(id)!);
}

export function copyImageAreaToPaper(
  editor: Editor,
  trace: Trace,
  source: TLImageShape,
  region: Rect,
) {
  if (!objectsUnderPaper(editor, trace, true).some((s) => s.id === source.id))
    throw new Error("The image moved. Select the area again.");
  const center = editor.getShapePageTransform(source).applyToPoint({
    x: (region.x + region.w / 2) * source.props.w,
    y: (region.y + region.h / 2) * source.props.h,
  });
  const id = putOnTrace(editor, source, trace.id, region, center);
  editor.select(trace.id);
  return id;
}
